import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  output,
  signal,
  viewChildren,
} from '@angular/core';
import { PartnerDealBriefingSectionResponse } from '@interfaces/partners/responses/partner-deal-briefing.response';
import { sectionDescription } from '@src/app/features/dashboard/commercial/pages/deal-detail/components/deal-briefing-tab/briefing-presenter';
import {
  BRIEFING_PROPOSED_STRUCTURE_KEY,
  BriefingFormat,
  BriefingRoadmapChild,
  BriefingRoadmapRow,
  buildRoadmap,
} from '@src/app/features/dashboard/commercial/pages/deal-detail/components/deal-briefing-tab/briefing-sections';
import { SharedModule } from '@src/app/shared/shared.module';

/** Subseção do roteiro com a descrição (no title). */
interface RoadmapChildItem extends BriefingRoadmapChild {
  description: string;
}

/** Linha do roteiro com o texto de apoio (descrição no title, ou o motivo da trava). */
interface RoadmapItem extends Omit<BriefingRoadmapRow, 'children'> {
  description: string;
  children: RoadmapChildItem[];
}

/**
 * O que cada formato traz, na linha embaixo do controle. Genérico de propósito (achado 5 da revisão do Q4): uma lista
 * escrita à mão desmentiria o roteiro quando o backend desligasse uma seção por configuração.
 */
const FORMAT_SUMMARIES: Record<BriefingFormat, string> = {
  // O briefing é documento interno (rodapé "Uso interno Legatus", decisão do Victor de 04/11/2026, card 3911).
  summary:
    'Todas as seções disponíveis, em até 3 páginas. Leitura rápida do deal para a equipe da Legatus.',
  custom:
    'Ligue ou desligue as seções opcionais no roteiro abaixo, inclusive a Investigação da empresa.',
};

/** Unidade do número que vem entre parênteses no título da seção ("Pilares (3)" vira "3 argumentos", A6). */
const DETAIL_UNITS: Record<string, string> = { pillars: 'argumentos' };

/**
 * Detalhe da seção com unidade quando o catálogo manda só o número; texto com unidade passa como veio. Destaques: o
 * agente escreve de 3 a 5 cards (regra da Q9), então "5 cards" do catálogo antigo vira "3 a 5 cards".
 */
function detailWithUnit(key: string, detail: string | null): string | null {
  if (key === 'highlights' && detail && /^5 cards$/i.test(detail.trim())) return '3 a 5 cards';
  if (!detail || !/^\d+$/.test(detail.trim())) return detail;
  return `${detail.trim()} ${DETAIL_UNITS[key] ?? 'itens'}`;
}

/** Ordem das opções do controle de formato, igual à do template. O Completo está travado na fase 1. */
const FORMAT_OPTIONS: readonly (BriefingFormat | 'full')[] = ['summary', 'full', 'custom'];

/**
 * Seletor de seções "Formato e roteiro" do Briefing (card 3878, opção 2 do protótipo, escolhida pelo Victor em
 * 04/11/2026).
 *
 * - **Formato**: controle segmentado Resumido (padrão, toda seção ligada, até 3 páginas), Completo (travado: o
 *   briefing de 12 páginas chega numa próxima versão, "em breve") e Personalizado (libera os interruptores das
 *   opcionais).
 * - **Roteiro do PDF**: as seções na ordem em que saem, numeradas, com "sempre" (obrigatória), "entra"/"fora"
 *   (opcional) ou o interruptor no Personalizado; a Estrutura proposta fica no lugar dela, travada, com o selo
 *   "em breve"; o separador "Página N" mostra onde a página provavelmente quebra (estimativa por peso de seção).
 *
 * Quem guarda a seleção é a aba; aqui só se desenha e se avisa o clique.
 */
@Component({
  selector: 'app-briefing-section-picker',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './briefing-section-picker.component.html',
  styleUrl: './briefing-section-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingSectionPickerComponent {
  readonly catalog = input<readonly PartnerDealBriefingSectionResponse[]>([]);
  readonly selected = input<readonly string[]>([]);
  readonly format = input<BriefingFormat>('summary');
  /** Prontidão bloqueada ou geração em andamento: nada muda. */
  readonly disabled = input(false);
  /**
   * Motivo de uma seção travada pela tela, por chave (ex.: Investigação sem `PARTNERDEALBRIEFINGINVESTIGATION_RUN`,
   * "Sem permissão para investigar"). Vale no lugar da descrição e troca o selo por "sem permissão".
   */
  readonly lockedReasons = input<Readonly<Record<string, string>>>({});

  readonly toggled = output<string>();
  readonly formatChosen = output<BriefingFormat>();

  /** Sem jargão de projeto (G5 da crítica): "em breve" no controle e a explicação no tooltip. */
  protected readonly completeLockedHint =
    'O briefing completo, de 12 páginas, chega numa próxima versão.';

  /** Botões do controle de formato, na ordem de {@link FORMAT_OPTIONS}, para o teclado mover o foco. */
  private readonly formatOptions = viewChildren<ElementRef<HTMLButtonElement>>('formatOption');

  protected readonly formatSummary = computed(() => FORMAT_SUMMARIES[this.format()]);

  protected readonly rows = computed<RoadmapItem[]>(() => {
    const byKey = new Map(this.catalog().map(s => [s.key, s]));
    const reasons = this.lockedReasons();
    return buildRoadmap(this.selected(), this.catalog()).map(row => {
      const section = byKey.get(row.key);
      const reason = row.state === 'locked' ? reasons[row.key] : undefined;
      return {
        ...row,
        detail: detailWithUnit(row.key, row.detail),
        description: reason ? `${reason}.` : section ? sectionDescription(section) : '',
        children: row.children.map(child => {
          const childSection = byKey.get(child.key);
          return {
            ...child,
            description: childSection ? sectionDescription(childSection) : '',
          };
        }),
      };
    });
  });

  protected readonly isCustom = computed(() => this.format() === 'custom');

  /** Linhas com subseções abertas (Investigação); fechadas por padrão, como pede a página aprovada. */
  protected readonly expanded = signal<ReadonlySet<string>>(new Set());

  protected isExpanded(key: string): boolean {
    return this.expanded().has(key);
  }

  protected toggleExpanded(event: Event, key: string): void {
    event.stopPropagation();
    this.expanded.update(keys => {
      const next = new Set(keys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  /** "8 de 11 subseções" na linha da mãe; com a mãe fora do documento, só o total (nenhuma entra). */
  protected childrenSummary(row: RoadmapItem): string {
    const total = row.children.filter(child => child.state !== 'locked').length;
    if (row.state === 'out' || row.state === 'locked') return `${total} subseções`;
    const marked = row.children.filter(child => child.state === 'in' || child.state === 'auto');
    return `${marked.length} de ${total} subseções`;
  }

  /** Clique numa subseção: só no Personalizado, com a mãe marcada, e só nas que se marcam sozinhas. */
  protected toggleChild(event: Event, row: RoadmapItem, child: RoadmapChildItem): void {
    event.stopPropagation();
    if (this.disabled() || !this.isCustom() || row.state !== 'in') return;
    if (child.state !== 'in' && child.state !== 'out') return;
    this.toggled.emit(child.key);
  }

  /**
   * Selo da seção travada: "sem permissão" quando a tela travou ({@link lockedReasons}); "em breve" na Estrutura
   * proposta; "Indisponível" em outra desligada por configuração.
   */
  protected lockedLabel(row: RoadmapItem): string {
    if (this.lockedReasons()[row.key]) return 'sem permissão';
    return row.key === BRIEFING_PROPOSED_STRUCTURE_KEY ? 'em breve' : 'Indisponível';
  }

  /**
   * Teclado do radiogroup (padrão ARIA): setas esquerda/cima e direita/baixo andam em roda, Home e End vão às
   * pontas. O foco anda por todas as opções; ao parar numa liberada ela é escolhida, no Completo travado só recebe o
   * foco (e mostra o aviso da fase 2).
   */
  protected onFormatKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;
    const buttons = this.formatOptions().map(ref => ref.nativeElement);
    const current = buttons.indexOf(event.target as HTMLButtonElement);
    if (current < 0) return;

    let next: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % buttons.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + buttons.length) % buttons.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = buttons.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    buttons[next].focus();
    const option = FORMAT_OPTIONS[next];
    if (option !== 'full') this.choose(option);
  }

  protected choose(format: BriefingFormat): void {
    if (this.disabled() || format === this.format()) return;
    this.formatChosen.emit(format);
  }

  /** Clique na linha do roteiro: só no Personalizado e só nas opcionais. */
  protected toggleRow(row: RoadmapItem): void {
    if (this.disabled() || !this.isCustom()) return;
    if (row.state !== 'in' && row.state !== 'out') return;
    this.toggled.emit(row.key);
  }

  protected toggleSwitch(event: Event, row: RoadmapItem): void {
    event.stopPropagation();
    this.toggleRow(row);
  }
}
